import { useEditManager } from "@/composables/edit-manager";
import { useProcessManager } from "@/composables/process-manager";
import { Artefact, ArtefactMeta } from "@/lib/artefacts";
import {
    HistoryEntryWithDate,
    useArtefactManager,
    useArtefactsStore,
    useArtefactStore,
    withContent,
} from "@/lib/artefacts/user";
import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch, watchEffect } from "vue";
import { useRoute } from "vue-router";

interface GroupedHistoryEntries {
    description: string;
    commits: HistoryEntryWithDate[];
}

const second = 1000;
const minute = 60 * second;
const hour = 60 * minute;
const day = 24 * hour;
function getWeek(date: Date) {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / day;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

function groupDates(historyEntries: HistoryEntryWithDate[]) {
    const today = new Date();
    const thisWeek = getWeek(today);
    const thisMonth = today.getMonth();

    const groups: { [groupName: string]: HistoryEntryWithDate[] } = {};
    historyEntries
        .sort((a, b) => {
            return a.author.date > b.author.date ? -1 : 1;
        })
        .forEach((entry) => {
            const {
                author: { date },
            } = entry;
            const week = getWeek(date);
            const month = date.getMonth();

            // First define the group
            let groupName: string;
            if (week === thisWeek) {
                groupName = "This week";
            } else if (week === thisWeek - 1) {
                groupName = "Last week";
            } else if (month === thisMonth) {
                groupName = "This month";
            } else {
                groupName = date.toLocaleString(undefined, {
                    month: "long",
                });
            }
            // Now add it to the proper group.
            if (groups[groupName]) {
                groups[groupName].push(entry);
            } else {
                groups[groupName] = [entry];
            }
        });

    const orderedGroups: GroupedHistoryEntries[] = [];
    Object.entries(groups).forEach(([groupName, commits]) => {
        orderedGroups.push({ description: groupName, commits });
    });

    return orderedGroups;
}

export const useHistoryStore = defineStore("history", () => {
    const route = useRoute();
    const isFetchingHistory = ref(false);
    const { get } = useArtefactStore();

    // When changes are shown, the version before the selected version needs to
    // be fetched as well.
    const isShowingChanges = ref(false);
    const artefact = ref<Artefact>();

    watchEffect(async () => {
        artefact.value = undefined;
        if (route.name === "processDefinitionPage") {
            const { definition } = useProcessManager();
            artefact.value = definition.value;
        } else if (route.name === "documentPage") {
            const { artefact: artefactMeta } = useArtefactManager();
            if (artefactMeta.value) {
                artefact.value = await get(artefactMeta.value);
            }
        }
    });

    const { isEditing } = useEditManager();
    const artefactsStore = useArtefactsStore();
    const history = ref<HistoryEntryWithDate[]>([]);

    const isViewingHistory = computed(
        () => !!("version" in route.params && route.params.version)
    );

    const loadHistory = async () => {
        // Avoid retrieving history when the history sidebar isn't opened yet.
        if (!isViewingHistory.value) {
            return;
        }

        if (!artefact.value) {
            return;
        }

        isFetchingHistory.value = true;

        try {
            history.value = await artefactsStore.getHistory(artefact.value);
        } catch (e) {
            // Sometimes the history doesn't immediately exist. Instead of
            // throwing an error, silently show an empty history. Something
            // we should fix. mystekz#138
            if (e instanceof Error && e.message.includes("404")) {
                history.value = [];
            } else {
                throw e;
            }
        } finally {
            isFetchingHistory.value = false;
        }
    };

    watch([isViewingHistory, () => artefact.value?.identity], () =>
        loadHistory()
    );

    /**
     * The most recent available in the history.
     */
    const lastCommit = computed(() => {
        if (!history.value) {
            return null;
        }

        return history.value[0];
    });

    /**
     * Get the currently selected commit.
     */
    const selectedCommit = computed(() => {
        if (route.params.version === "current") {
            return history.value[0];
        } else {
            return history.value.find(
                (entry) => entry.id === route.params.version
            );
        }
    });

    /**
     * The commit previous to the selected commit.
     */
    const previousCommit = computed(() => {
        const index = history.value.findIndex(
            (entry) => entry.id === selectedCommit.value?.id
        );

        if (index === -1) {
            return;
        }

        if (index >= history.value.length - 1) {
            return;
        }

        return history.value[index + 1];
    });

    const groupedHistoryEntries = computed<GroupedHistoryEntries[]>(() => {
        if (!history.value) {
            return [];
        }

        return groupDates(history.value);
    });

    const isViewingLatestVersion = computed(() => {
        if (!history.value) {
            return true;
        }

        // While there is either no history or we're still fetching the
        // history, assume we're not viewing an older version.
        if (history.value.length === 0) {
            return true;
        }

        if (!isViewingHistory.value) {
            return true;
        }

        if (route.params.version === "current") {
            return true;
        }

        // When we haven't modified the artefact yet and the param version is
        // the same as the latest commit, we're viewing the latest version.
        if (
            !isEditing.value &&
            selectedCommit.value?.id === lastCommit.value?.id
        ) {
            return true;
        }

        return false;
    });

    const selectedHistoricalArtefactMeta = computed<ArtefactMeta>(() => {
        // No need to re-fetch the original artefact when we're viewing the
        // latest version.
        if (isViewingLatestVersion.value) {
            return;
        }

        if (!isViewingHistory.value) {
            return;
        }

        if (!artefact.value) {
            return;
        }

        if (!selectedCommit.value) {
            return;
        }

        const copyOfArtefact = { ...artefact.value };
        copyOfArtefact.revision = selectedCommit.value.id;

        return copyOfArtefact;
    });

    const {
        artefact: _selectedHistoricalArtefact,
        isFetching: isFetchingHistoricalArtefact,
    } = withContent(selectedHistoricalArtefactMeta);

    const selectedArtefact = computed(() => {
        // No need to fetch the historical artefact when we're viewing the
        // latest version.
        if (isViewingLatestVersion.value) {
            return artefact.value;
        }

        return _selectedHistoricalArtefact.value;
    });

    // One entry before the selected historical artefact.
    const comparisonArtefactMeta = computed<ArtefactMeta>(() => {
        if (!selectedArtefact.value) {
            return;
        }

        if (!previousCommit.value) {
            return;
        }

        const copyOfArtefact = { ...artefact.value } as ArtefactMeta;
        copyOfArtefact.revision = previousCommit.value.id;

        return copyOfArtefact;
    });

    const {
        artefact: comparisonArtefact,
        isFetching: isFetchingOlderArtefact,
    } = withContent(comparisonArtefactMeta);

    return {
        selectedCommit,
        previousCommit,
        lastCommit,
        groupedHistoryEntries,
        isViewingLatestVersion,
        isViewingHistory,
        loadHistory,
        selectedArtefact,
        comparisonArtefact,
        isFetchingHistory,
        isShowingChanges,
        isFetchingHistoricalArtefact: computed(
            () =>
                !!(
                    isFetchingHistoricalArtefact.value ||
                    isFetchingOlderArtefact.value
                )
        ),
    };
});

export const useHistoryManager = () => storeToRefs(useHistoryStore());
